// One game's numbers for both teams (/match-stats; the Match module, Ben 2026-10-31): the bars
// down the Match card.

import { MATCH_STAT_KEYS, type MatchStatKey, type MatchStatsResponse, type MatchStatsTeam } from "@catbento/shared";
import type { LiveApi } from "./api.js";

export async function loadMatchStats(api: LiveApi, matchId: number): Promise<MatchStatsTeam[]> {
  const { teams } = await api.get<MatchStatsResponse>(`/match-stats?matchId=${matchId}`);
  return teams;
}

const LABELS: Record<MatchStatKey, string> = {
  xg: "xG",
  shots: "Shots",
  shotsOnTarget: "Shots on target",
  touchesInBox: "Touches in the opposition box",
  corners: "Corners",
  aerialsWon: "Aerial duels won",
  saves: "Goalkeeper saves",
  fouls: "Fouls committed",
};

// The engine counts these without penalties (its definitions, shared with the Stats tiles).
const NOTES: Partial<Record<MatchStatKey, string>> = {
  shotsOnTarget: "Penalties not counted",
  saves: "Shots on target faced minus goals from them; penalties not counted",
};

export interface MatchStatRow {
  key: MatchStatKey;
  label: string;
  note: string | null;
  home: string;
  away: string;
  /** Each side's share of the two (0–1): the length of its bar. 0 / 0 → both 0. */
  homeShare: number;
  awayShare: number;
  /** The bigger number, bold ("home" / "away"; null when level). */
  leader: "home" | "away" | null;
}

/** The card's bar rows, home first. A stat either side lacks is left out. Pure. */
export function matchStatRows(home: MatchStatsTeam, away: MatchStatsTeam): MatchStatRow[] {
  const rows: MatchStatRow[] = [];
  for (const key of MATCH_STAT_KEYS) {
    const h = home.values[key];
    const a = away.values[key];
    if (h === null || a === null) continue;
    const total = h + a;
    const fmt = (v: number) => (key === "xg" ? v.toFixed(2) : String(Math.round(v)));
    rows.push({
      key,
      label: LABELS[key],
      note: NOTES[key] ?? null,
      home: fmt(h),
      away: fmt(a),
      homeShare: total > 0 ? h / total : 0,
      awayShare: total > 0 ? a / total : 0,
      leader: h > a ? "home" : a > h ? "away" : null,
    });
  }
  return rows;
}

/** The two teams in home / away order, or null if the answer doesn't have both. Pure. */
export function homeAway(teams: readonly MatchStatsTeam[], homeId: number, awayId: number): [MatchStatsTeam, MatchStatsTeam] | null {
  const home = teams.find((t) => t.teamId === homeId);
  const away = teams.find((t) => t.teamId === awayId);
  return home && away ? [home, away] : null;
}
